import Link from 'next/link';
import { MessageSquare, Target } from 'lucide-react';
import { useBasePath } from '../../../lib/use-base-path';
import { useTranslation } from '../../../lib/i18n';
import type { GoalOrigin } from '../../../lib/task-origin';
import { Button } from '../ui';
import styles from './TaskOriginLine.module.css';

// "From goal — Autumn book club": one quiet line saying where a task came from, with the goal's name as
// the link to it. Rendered on the task ROW, on a collapsed goal fold's header, and in the task DETAIL, so
// the answer to "why is this row here" is in the same place and the same weight wherever you meet it.
//
// **It sits ABOVE the card, not inside it.** The whole row is a button element (the card opens the task's
// detail), and an anchor nested inside a button is invalid HTML that browsers resolve unpredictably — the
// same constraint that puts the row's overflow menu outside the Card. So the line is a sibling in the row
// wrapper, which also reads correctly: it is a header FOR the row, not a field of it.
//
// The glyph is `target`, the goals mark, chosen to sit beside the routines mark (`repeat-2`) the fold
// header already wears — two origins, two marks, one line shape.
/**
 * The same line for a task whose origin is the CHAT it was born in — every task has one, where a goal is
 * only some tasks' origin (see `taskChatOrigin`). Rendered only when that conversation is one the reader
 * can actually be dropped into, so the name always has a destination.
 *
 * It is a BUTTON, not a Link: a conversation is opened in place (the side chat swaps threads) rather than
 * navigated to, so there is no href to give an anchor. The goal variant above stays a real Link precisely
 * because a goal IS a route.
 */
export function TaskChatOriginLine({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();

  return (
    <div className={styles.line}>
      {/* The DS text Button, not a hand-rolled one: a conversation is opened in place (there is no route
          to a thread, so no href and no anchor), and this repo's button guard exists because every
          hand-rolled control drifted. `tiny` is the smallest DS size and the closest to the goal line's
          own weight; the glyph rides its prefix slot so both origin lines read as one shape. */}
      <Button
        variant="text"
        size="tiny"
        startIcon={<MessageSquare size={14} aria-hidden />}
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        {t('tasks.origin.chat')}
      </Button>
    </div>
  );
}

export function TaskOriginLine({ origin, onOpen }: { origin: GoalOrigin; onOpen?: () => void }) {
  const { t } = useTranslation();
  const basePath = useBasePath();

  return (
    <div className={styles.line}>
      <Target size={14} aria-hidden className={styles.glyph} />
      <span className={styles.label}>{t('tasks.origin.goal')}</span>
      {origin.href ? (
        // `stopPropagation`, because the card behind this line opens the TASK: without it a click on the
        // goal's name would navigate and open the task detail at the same time.
        <Link
          href={`${basePath}${origin.href}`}
          className={styles.name}
          dir="auto"
          onClick={(event) => {
            event.stopPropagation();
            onOpen?.();
          }}
        >
          {origin.title}
        </Link>
      ) : (
        // No resolvable workspace slug — see `GoalOrigin.href`. The goal is still named; it just doesn't
        // pretend to be openable.
        <span className={styles.name} dir="auto">
          {origin.title}
        </span>
      )}
    </div>
  );
}
